import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import simpleQuestionsApi from '../services/simpleQuestionsApi';
import { STATUS_CONFIG } from '../constants';
import { formatDate } from '../utils';
import AttachmentList from '../components/AttachmentList';
import { useGlobalStore } from '../../../state/globalStore';

const SimpleQuestionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useGlobalStore();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const loadQuestion = useCallback(async () => {
    setLoading(true);
    
    try {
      // Try to load from API first
      const result = await simpleQuestionsApi.getQuestionById(id);
      
      if (result.success) {
        setQuestion(result.data);
      } else {
        // Fallback to mock data if API fails
        console.info('Using demo data - API not configured yet');
        const mockQuestions = simpleQuestionsApi.getMockQuestions();
        const foundQuestion = mockQuestions.find(q => q.id === parseInt(id));
        if (foundQuestion) {
          setQuestion(foundQuestion);
        }
      }
    } catch (error) {
      console.info('Using demo data - API not available');
      // Fallback to mock data
      const mockQuestions = simpleQuestionsApi.getMockQuestions();
      const foundQuestion = mockQuestions.find(q => q.id === parseInt(id));
      if (foundQuestion) {
        setQuestion(foundQuestion);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadQuestion();
  }, [loadQuestion]);

  // Load user role for dealroom questions
  useEffect(() => {
    const loadUserRole = async () => {
      if (question && question.module_type === 'dataroom' && question.project_id && user?.id) {
        try {
          const { supabase } = await import('../../../config');
          const { data, error } = await supabase
            .from('dataroom_users')
            .select('role')
            .eq('user_id', user.id)
            .eq('project_id', question.project_id)
            .single();

          if (error) throw error;
          setUserRole(data?.role || null);
        } catch (error) {
          console.error('Error loading user role:', error);
          setUserRole(null);
        }
      }
    };

    loadUserRole();
  }, [question, user?.id]);

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.open;
  };


  const getTimeSince = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return `${Math.floor(diffInDays / 7)}w ago`;
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const getContextColor = (moduleType) => {
    switch (moduleType) {
      case 'dataroom': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'capraise': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'equity_manager': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const handleResponseSubmit = async (e) => {
    e.preventDefault();
    if (!responseText.trim()) return;

    setSubmitting(true);

    const responseData = {
      response_text: responseText,
      user_id: user?.id || user?.uid || 'anonymous',
      user_name: userProfile?.full_name || user?.email || 'Unknown',
      is_from_admin: userProfile?.is_super_admin || false
    };

    try {
      // Try to create via API
      const result = await simpleQuestionsApi.addResponse(id, responseData);
      
      if (result.success) {
        // Add response to question
        setQuestion(prev => ({
          ...prev,
          responses: [...(prev.responses || []), result.data]
        }));
      } else {
        // Fallback: add locally if API fails
        const newResponse = {
          id: Date.now(),
          ...responseData,
          created_at: new Date().toISOString()
        };
        setQuestion(prev => ({
          ...prev,
          responses: [...(prev.responses || []), newResponse]
        }));
        console.info('Response added locally - API not configured yet');
      }
    } catch (error) {
      // Fallback: add locally if API fails
      const newResponse = {
        id: Date.now(),
        ...responseData,
        created_at: new Date().toISOString()
      };
      setQuestion(prev => ({
        ...prev,
        responses: [...(prev.responses || []), newResponse]
      }));
      console.info('Response added locally - API not available');
    }

    // Clear form
    setResponseText('');
    setSubmitting(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdatingStatus(true);
    
    try {
      // Try to update via API
      const result = await simpleQuestionsApi.updateQuestionStatus(id, newStatus);
      
      if (result.success) {
        setQuestion(prev => ({ ...prev, status: newStatus }));
      } else {
        // Fallback: update locally if API fails
        setQuestion(prev => ({ ...prev, status: newStatus }));
        console.info('Status updated locally - API not configured yet');
      }
    } catch (error) {
      // Fallback: update locally if API fails
      setQuestion(prev => ({ ...prev, status: newStatus }));
      console.info('Status updated locally - API not available');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate('/questions/list/all')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Questions
        </button>
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">❓</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Question not found</h3>
          <p className="text-gray-500">The question you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/questions/list/all')}
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Questions
      </button>

      {/* Question Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        {/* Metadata Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {/* Time */}
            <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded">
              {getTimeSince(question.created_at)}
            </span>
            
            {/* Priority */}
            <span className={`text-sm px-3 py-1 rounded border ${getPriorityColor(question.priority)}`}>
              {question.priority === 'high' ? 'High Priority' : 'Normal'}
            </span>
            
            {/* Status */}
            {(() => {
              const statusConfig = getStatusConfig(question.status);
              const StatusIcon = statusConfig.icon;
              return (
                <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium border ${statusConfig.bg} ${statusConfig.text}`}>
                  <StatusIcon className="h-4 w-4 mr-1" />
                  {statusConfig.label}
                </span>
              );
            })()}
            
            {/* Context */}
            <span className={`text-sm px-3 py-1 rounded border ${getContextColor(question.module_type)}`}>
              {question.module_type === 'equity_manager' ? 'Equity' : 
               question.module_type === 'dataroom' ? 'Dataroom' :
               question.module_type === 'capraise' ? 'CapRaise' : 'General'}
            </span>
          </div>
        </div>

        {/* Dealroom Context Info */}
        {question.module_type === 'dataroom' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">Dealroom Context</h3>
                <div className="text-sm text-blue-800">
                  {question.module_reference_name || 'Dealroom'}
                  {question.folder_name && ` > ${question.folder_name}`}
                  {question.file_name && ` > ${question.file_name}`}
                </div>
              </div>
              {userRole && (
                <div className="ml-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    userRole === 'creator' 
                      ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                      : 'bg-green-100 text-green-800 border border-green-200'
                  }`}>
                    {userRole === 'creator' ? 'Creator' : 'Viewer'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Question Content */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {question.title}
          </h1>
          <div className="prose prose-sm max-w-none">
            <div 
              className="text-gray-700 text-lg leading-relaxed"
              dangerouslySetInnerHTML={{ 
                __html: question.description?.replace(/\\n/g, '<br>').replace(/\n/g, '<br>') 
              }}
            />
          </div>
        </div>

        {/* Footer Info */}
        <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100">
          <div className="flex items-center space-x-4">
            <span>By {question.user_name || 'Unknown'}</span>
            <span>{formatDate(question.created_at)}</span>
            {question.category && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">
                {question.category}
              </span>
            )}
          </div>
          
          {/* Response Count */}
          {question.responses && question.responses.length > 0 && (
            <span className="text-blue-600 font-medium">
              {question.responses.length} response{question.responses.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Status Update Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-base font-semibold text-gray-900">Update Status</h2>
            {(() => {
              const statusConfig = getStatusConfig(question.status);
              const StatusIcon = statusConfig.icon;
              return (
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${statusConfig.bg} ${statusConfig.text}`}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusConfig.label}
                </span>
              );
            })()}
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              id="status-select"
              value={question.status}
              onChange={(e) => handleStatusUpdate(e.target.value)}
              disabled={updatingStatus}
              className="px-3 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm"
            >
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                <option key={status} value={status}>
                  {config.label}
                </option>
              ))}
            </select>
            {updatingStatus && (
              <span className="text-xs text-gray-500">Updating...</span>
            )}
          </div>
        </div>
      </div>

      {/* Responses Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <MessageSquare className="h-5 w-5 text-blue-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">
            Responses ({question.responses?.length || 0})
          </h2>
        </div>

        {question.responses && question.responses.length > 0 ? (
          <div className="space-y-3">
            {question.responses.map(response => (
              <div key={response.id} className={`p-3 rounded-lg border ${
                response.is_from_admin 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                {/* Response Metadata Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {/* Time */}
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                      {getTimeSince(response.created_at)}
                    </span>
                    
                    {/* Author */}
                    <span className="font-semibold text-gray-900 text-sm">
                      {response.user_name}
                    </span>
                    
                    {/* Admin Badge */}
                    {response.is_from_admin && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded border border-blue-200">
                        Admin
                      </span>
                    )}
                  </div>
                  
                  {/* Full Date */}
                  <span className="text-xs text-gray-500">
                    {formatDate(response.created_at)}
                  </span>
                </div>

                {/* Response Content */}
                <div className="prose max-w-none">
                  <div 
                    className={`text-sm leading-relaxed ${
                      response.is_from_admin ? 'text-blue-900' : 'text-gray-800'
                    }`}
                    dangerouslySetInnerHTML={{ __html: response.response_text }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-2">💬</div>
            <p className="text-gray-500">No responses yet. Be the first to reply!</p>
          </div>
        )}

        {/* Response Form */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-md font-semibold text-gray-900 mb-3">Add a Response</h3>
          <form onSubmit={handleResponseSubmit} className="space-y-4">
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Type your response here..."
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={submitting}
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !responseText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Posting...' : 'Post Response'}
              </button>
            </div>
          </form>
        </div>

        {/* Attachments Section */}
        <div className="mt-8">
          <AttachmentList 
            questionId={id} 
            userId={user?.id || 'current_user'}
            canUpload={true}
            canDelete={true}
            showUploadButton={true}
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleQuestionDetailPage;
